import React, { useState } from "react";
import { ShieldAlert, RefreshCw, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { VpnDetailsModal } from "./VpnDetailsModal";

export const VpnAlertBanner: React.FC = () => {
  const { vpnDetected, networkInfo, user, recheckNetworkConnection, recheckingNetwork } = useAuth() as any;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);

  if (!vpnDetected) return null;

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className="sticky top-0 z-40 flex min-h-[40px] flex-wrap items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/80 dark:text-amber-200 shadow-sm"
      >
        <div className="flex items-center gap-2.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-200/80 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
            <ShieldAlert className="h-4 w-4" />
          </span>
          <span className="font-medium">
            <strong>VPN Active:</strong> Read-Only Mode enabled. You can inspect all dashboards and timezone settings, but cannot save changes or schedule calls while connected to a VPN.
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white/80 px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-white dark:border-amber-800 dark:bg-amber-900/60 dark:text-amber-100 dark:hover:bg-amber-900"
          >
            <Info className="h-3.5 w-3.5" />
            <span>Details</span>
          </button>

          {recheckNetworkConnection && (
            <button
              type="button"
              disabled={recheckingNetwork}
              onClick={() => recheckNetworkConnection()}
              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
            >
              <RefreshCw className={`h-3 w-3 ${recheckingNetwork ? "animate-spin" : ""}`} />
              <span>{recheckingNetwork ? "Checking..." : "Re-check"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Small notification popup modal on VPN detection */}
      {!popupDismissed && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vpn-initial-popup-title"
        >
          <div className="w-full max-w-sm rounded-2xl border border-amber-200 bg-white p-5 shadow-2xl dark:border-amber-900/60 dark:bg-slate-900 animate-in zoom-in-95 duration-200">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/80 dark:text-amber-300">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <h3 id="vpn-initial-popup-title" className="text-sm font-bold text-slate-900 dark:text-white">
                  VPN Connection Detected
                </h3>
                <p className="text-xs font-medium text-amber-800 dark:text-amber-400">
                  Read-Only Mode Active
                </p>
              </div>
            </div>

            <p className="mt-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              You are connected through a VPN or proxy tunnel. You can freely inspect dashboards, metrics, and timezones, but configuration changes and write actions are restricted.
            </p>
            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
              Please disconnect your VPN or contact your TechLadder administrator for exemptions.
            </p>

            <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  setPopupDismissed(true);
                  setDetailsOpen(true);
                }}
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Diagnostics
              </button>
              <button
                type="button"
                onClick={() => setPopupDismissed(true)}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 shadow-sm"
              >
                Acknowledge (Read-Only)
              </button>
            </div>
          </div>
        </div>
      )}

      <VpnDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        networkInfo={networkInfo}
        userEmail={user?.email}
        onRecheck={recheckNetworkConnection}
        rechecking={recheckingNetwork}
      />
    </>
  );
};

export default VpnAlertBanner;
